import { type GridPosition } from '../../machine/state/types.ts';
import { createEffect } from '../utils/create-effect.ts';

export interface RowSelectionHandlePingEffectDeps {
  resolveRowPositionToEntryId(position: GridPosition): string | undefined;
  notifyRowSelectionChanged(entryId: string): void;
}

/**
 * Invalidates selection-driven row handles without waking unrelated rows.
 *
 * Selection changes refresh the previously and currently focused rows because
 * selected membership and movement bounds can change without moving focus.
 * Focus-only changes refresh both endpoints. The cache belongs to one effect
 * instance and is discarded with the recyclable selections machine.
 */
export const createRowSelectionHandlePingEffect = createEffect(
  ({
    resolveRowPositionToEntryId,
    notifyRowSelectionChanged
  }: RowSelectionHandlePingEffectDeps) => {
    let previouslyFocusedRowEntryId: string | undefined;

    return {
      deltaTypes: ['selectionReplaced', 'selectionModified', 'focusChanged'],

      onDelta(delta, state) {
        const focusedRowEntryId =
          state.mode === 'row' && state.focusedCell
            ? resolveRowPositionToEntryId(state.focusedCell)
            : undefined;
        const previousFocusedRowEntryId =
          delta.kind === 'focusChanged' && state.mode === 'row' && delta.prev
            ? resolveRowPositionToEntryId(delta.prev)
            : previouslyFocusedRowEntryId;
        const rowEntryIdsToNotify = new Set(
          [previousFocusedRowEntryId, focusedRowEntryId].filter(
            (entryId): entryId is string => entryId !== undefined
          )
        );

        for (const entryId of rowEntryIdsToNotify) {
          notifyRowSelectionChanged(entryId);
        }

        previouslyFocusedRowEntryId = focusedRowEntryId;
      }
    };
  }
);
